// ───────────────────────────────────────────────────
// src/features/stock/components/ProductAttributes.tsx
// ───────────────────────────────────────────────────

import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// (A) “All attributes” come from attributeApi.ts
import {
  Attribute,
  fetchAttributesApi,
} from '../api/attributeApi';

// (B) product→[attribute_id] map & linkAttributeApi come from productApi.ts
import {
  fetchProductAttributesMap,
  linkAttributeApi,
  ProductAttributesMap,
} from '../api/productApi';

//
// Full (normalized) attribute shape is exactly the same as “Attribute”:
//
type FullAttribute = Attribute;

//
// Payload for linking/unlinking:
//
interface LinkAttributePayload {
  product_id: number;
  attribute_id: number;
  unlink?: boolean;
}

interface ProductAttributesProps {
  productId: number;
}

export default function ProductAttributes({ productId }: ProductAttributesProps) {
  const queryClient = useQueryClient();

  //
  // 1) Fetch “all attributes” once
  //
    const {
    data: allAttrsRaw = [],
    isLoading: isAllLoading,
    isError: isAllError,
    error: allError,
    } = useQuery<Attribute[], Error>({
    queryKey: ['allAttributes'],
    queryFn: fetchAttributesApi,
    staleTime: 5 * 60_000,
    })

  //
  // 2) Fetch the complete product→[attribute_ids] map
  //
  const {
    data: productAttrMap = {},
    isLoading: isMapLoading,
    isError: isMapError,
    error: mapError,
  } = useQuery<ProductAttributesMap, Error>({
    queryKey: ['productAttributesMap'],
    queryFn: fetchProductAttributesMap,
    staleTime: 5 * 60_000, // 5 minutes
  });

  //
  // 3) Mutation: link OR unlink => always POST to /service_stock/link_attribute
  //
  const linkMutation = useMutation<
    { product_id: number; attribute_id: number; unlinked: boolean },
    Error,
    LinkAttributePayload
  >({
    mutationFn: (payload) => linkAttributeApi(payload),
    onSuccess: () => {
      // Re‐fetch only the productAttributesMap (so that Linked set is up‐to‐date)
      queryClient.invalidateQueries({ queryKey: ['productAttributesMap'] });
    },
  });

  //
  // 4) Build a Set of “already linked” attribute IDs for this product
  //
  const linkedIdsForThis = React.useMemo(() => {
    return new Set<number>((productAttrMap[productId] ?? []));
  }, [productAttrMap, productId]);

  //
  // 5) Loading / error UI
  //
  if (isAllLoading || isMapLoading) {
    return <div className="text-sm text-neutral-400">Loading attributes…</div>;
  }
  if (isAllError) {
    return (
      <div className="text-sm text-rose-400">
        Error fetching all attributes: {allError!.message}
      </div>
    );
  }
  if (isMapError) {
    return (
      <div className="text-sm text-rose-400">
        Error fetching linked-attributes: {mapError!.message}
      </div>
    );
  }

  //
  // 6) If there are no attributes at all, render nothing
  //
  if (allAttrsRaw.length === 0) {
    return null;
  }

  //
  // 7) Render each attribute in a flat list
  //    • if NOT linked ⇒ green “+” to link
  //    • if Linked     ⇒ red “–” to unlink
  //
  return (
    <div className="w-full bg-neutral-950 p-2">
      <ul className="space-y-1">
        {allAttrsRaw.map((attr) => {
          const isLinked = linkedIdsForThis.has(attr.id_attribute);

          return (
            <li key={attr.id_attribute} className="flex items-center px-2 py-1">
              {/* — If linked: show “–” in red, unlink on click */}
              {isLinked && (
                <button
                  onClick={() =>
                    linkMutation.mutate({
                      product_id: productId,
                      attribute_id: attr.id_attribute,
                      unlink: true, // <— This flag tells the backend “unlink”
                    })
                  }
                  className={`
                    flex h-6 w-6 items-center justify-center
                    rounded bg-neutral-950 text-red-500 hover:bg-neutral-800
                    ${linkMutation.status === 'pending' ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                  disabled={linkMutation.status === 'pending'}
                  title="Unlink this attribute"
                >
                  &minus;
                </button>
              )}

              {/* — If not linked: show “+” in green, link on click */}
              {!isLinked && (
                <button
                  onClick={() =>
                    linkMutation.mutate({
                      product_id: productId,
                      attribute_id: attr.id_attribute,
                      // no “unlink” flag → this is a link
                    })
                  }
                  className={`
                    flex h-6 w-6 items-center justify-center
                    rounded bg-neutral-950 text-green-400 hover:bg-neutral-800
                    ${linkMutation.status === 'pending' ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                  disabled={linkMutation.status === 'pending'}
                  title="Link this attribute"
                >
                  +
                </button>
              )}

              {/* — Finally, the attribute name in white if linked, dim grey if not */}
              <span
                className={`${
                  isLinked ? 'text-neutral-200' : 'text-neutral-700'
                } ${isLinked ? '' : 'ml-2'}`}
              >
                {attr.attribute_name}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
